import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchCoursePage } from "@/api/trainerAuth";
import { checkCourseEnrollment } from "@/api/studentAuth";

export default function StudentCourseWatch() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState(new Set());
  const [progress, setProgress] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        
        // تحقق الاشتراك
        const enrolled = await checkCourseEnrollment(courseId);
        if (!enrolled) {
          navigate(`/student/courses/${courseId}/details`);
          return;
        }

        setIsEnrolled(true);

        // جلب بيانات الكورس
        const data = await fetchCoursePage(courseId);
        setCourse(data.course);
        setSections(data.sections || []);
        setVideos(data.videos || []);

        // حساب التقدم
        calculateProgress(data.videos || []);

        // اختيار أول فيديو افتراضيًا
        if (data.sections?.[0]?.videos?.[0]) {
          setSelectedVideo(data.sections[0].videos[0]);
        }

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [courseId, navigate]);

  const calculateProgress = (videosList) => {
    const watchedCount = videosList.filter(v => v.watched).length;
    const totalCount = videosList.length;
    const progressPercent = totalCount > 0 ? (watchedCount / totalCount) * 100 : 0;
    setProgress(Math.round(progressPercent));
  };

  const handleVideoSelect = async (video) => {
    setSelectedVideo(video);
    
    // وضع علامة كمشاهدة إذا لم يكن مشاهدًا
    if (!video.watched) {
      try {
        // هنا يمكنك إضافة API خاص بوضع علامة كمشاهدة إذا كان موجوداً
        // بدلاً من ذلك، سنقوم بتحديث الحالة المحلية فقط
        const updatedVideos = videos.map(v => 
          v.id === video.id ? { ...v, watched: true } : v
        );
        setVideos(updatedVideos);
        calculateProgress(updatedVideos);
        setWatchedVideos(prev => new Set([...prev, video.id]));
      } catch (error) {
        console.error('Failed to mark video as watched:', error);
      }
    }
  };

  const handleVideoEnded = async () => {
    if (selectedVideo && !selectedVideo.watched) {
      try {
        const updatedVideos = videos.map(v => 
          v.id === selectedVideo.id ? { ...v, watched: true } : v
        );
        setVideos(updatedVideos);
        calculateProgress(updatedVideos);
        setWatchedVideos(prev => new Set([...prev, selectedVideo.id]));
      } catch (error) {
        console.error('Failed to mark video as watched:', error);
      }
    }
  };

  const navigateToNextVideo = () => {
    const currentVideo = selectedVideo;
    if (!currentVideo) return;

    // البحث عن الفيديو التالي في نفس القسم
    const currentSection = sections.find(s => s.id === currentVideo.section_id);
    if (!currentSection) return;

    const sectionVideos = videos.filter(v => v.section_id === currentSection.id);
    const currentIndex = sectionVideos.findIndex(v => v.id === currentVideo.id);
    
    if (currentIndex < sectionVideos.length - 1) {
      handleVideoSelect(sectionVideos[currentIndex + 1]);
    } else {
      // الانتقال للقسم التالي
      const currentSectionIndex = sections.findIndex(s => s.id === currentSection.id);
      if (currentSectionIndex < sections.length - 1) {
        const nextSection = sections[currentSectionIndex + 1];
        const nextSectionVideos = videos.filter(v => v.section_id === nextSection.id);
        if (nextSectionVideos.length > 0) {
          handleVideoSelect(nextSectionVideos[0]);
        }
      }
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>جاري تحميل محتوى الكورس...</p>
      </div>
    );
  }

  if (!isEnrolled || !course) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* الهيدر */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.courseInfo}>
            <button 
              style={styles.backButton}
              onClick={() => navigate("/student/my-courses")}
            >
              ↩ العودة
            </button>
            <h1 style={styles.courseTitle}>{course.title}</h1>
          </div>
          <div style={styles.headerStats}>
            <div style={styles.progressSection}>
              <span style={styles.progressLabel}>إتمام الكورس</span>
              <div style={styles.progressContainer}>
                <div style={styles.progressBar}>
                  <div 
                    style={{
                      ...styles.progressFill,
                      width: `${progress}%`,
                      background: progress === 100 ? '#4CAF50' : '#2196F3'
                    }}
                  ></div>
                </div>
                <span style={styles.progressText}>{progress}%</span>
              </div>
            </div>
            <button 
              style={styles.sidebarToggle}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? "إظهار القائمة" : "إخفاء القائمة"}
            >
              {sidebarCollapsed ? "☰" : "✕"}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.content}>
        {/* القائمة الجانبية */}
        <aside style={{
          ...styles.sidebar,
          width: sidebarCollapsed ? '0' : '320px',
          padding: sidebarCollapsed ? '0' : '20px',
          overflow: sidebarCollapsed ? 'hidden' : 'visible',
        }}>
          <div style={styles.sidebarContent}>
            {/* معلومات الكورس */}
            <div style={styles.courseMeta}>
              <div style={styles.metaItem}>
                <span style={styles.metaIcon}>👨‍🏫</span>
                <span style={styles.metaText}>{course.trainer || "غير محدد"}</span>
              </div>
              <div style={styles.metaItem}>
                <span style={styles.metaIcon}>📊</span>
                <span style={styles.metaText}>{course.level || "جميع المستويات"}</span>
              </div>
              <div style={styles.metaItem}>
                <span style={styles.metaIcon}>🎬</span>
                <span style={styles.metaText}>{videos.length} فيديو</span>
              </div>
              <div style={styles.metaItem}>
                <span style={styles.metaIcon}>👥</span>
                <span style={styles.metaText}>{course.students_count || 0} طالب</span>
              </div>
            </div>

            {/* قائمة الأقسام والفيديوهات */}
            <div style={styles.sectionsList}>
              {sections.map((section, sectionIndex) => (
                <div key={section.id} style={styles.sectionCard}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                      <span style={styles.sectionNumber}>{sectionIndex + 1}.</span>
                      {section.title}
                    </h3>
                    <span style={styles.sectionCount}>
                      {videos.filter(v => v.section_id === section.id).length} فيديو
                    </span>
                  </div>
                  
                  <div style={styles.videosList}>
                    {videos
                      .filter(video => video.section_id === section.id)
                      .map((video, videoIndex) => (
                        <div 
                          key={video.id}
                          style={{
                            ...styles.videoItem,
                            ...(selectedVideo?.id === video.id && styles.videoItemActive),
                          }}
                          onClick={() => handleVideoSelect(video)}
                        >
                          <div style={styles.videoInfo}>
                            <div style={styles.videoHeaderItem}>
                              <span style={styles.videoIcon}>
                                {video.watched || watchedVideos.has(video.id) ? "✅" : "▶️"}
                              </span>
                              <span style={styles.videoTitleItem}>
                                {videoIndex + 1}. {video.title}
                              </span>
                            </div>
                            {video.duration && (
                              <span style={styles.videoDuration}>{video.duration}</span>
                            )}
                          </div>
                          
                          <div style={styles.videoMeta}>
                            <span style={styles.videoStatus}>
                              {video.watched || watchedVideos.has(video.id) ? "تمت المشاهدة" : "غير مشاهد"}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* المحتوى الرئيسي */}
        <main style={styles.mainContent}>
          {/* مشغل الفيديو */}
          {selectedVideo ? (
            <div style={styles.videoPlayerSection}>
              <div style={styles.playerHeader}>
                <h2 style={styles.playerTitle}>{selectedVideo.title}</h2>
                <div style={styles.playerActions}>
                  <button 
                    style={styles.nextButton}
                    onClick={navigateToNextVideo}
                  >
                    الفيديو التالي →
                  </button>
                  <span style={styles.videoStatusBadge}>
                    {selectedVideo.watched || watchedVideos.has(selectedVideo.id) ? "✅ تمت المشاهدة" : "🆕 جديد"}
                  </span>
                </div>
              </div>
              
              <div style={styles.videoContainer}>
                {selectedVideo.video_url ? (
                  <video
                    key={selectedVideo.id}
                    src={selectedVideo.video_url}
                    controls
                    autoPlay
                    style={styles.videoPlayer}
                    onEnded={handleVideoEnded}
                  />
                ) : (
                  <div style={styles.noVideo}>
                    <span style={styles.noVideoIcon}>🎬</span>
                    <p style={styles.noVideoText}>لا يوجد فيديو متاح</p>
                  </div>
                )}
              </div>

              {/* وصف الفيديو */}
              <div style={styles.descriptionSection}>
                <h3 style={styles.descriptionTitle}>عن هذا الدرس</h3>
                <p style={styles.descriptionText}>
                  {selectedVideo.description || "لا يوجد وصف متاح لهذا الفيديو."}
                </p>
              </div>
            </div>
          ) : (
            <div style={styles.noVideoSelected}>
              <div style={styles.noVideoSelectedIcon}>📺</div>
              <h3 style={styles.noVideoSelectedTitle}>اختر فيديو للمشاهدة</h3>
              <p style={styles.noVideoSelectedText}>انقر على أي فيديو من القائمة الجانبية لبدء المشاهدة</p>
            </div>
          )}

          {/* المواد المساندة */}
          {selectedVideo && (
            <div style={styles.resourcesSection}>
              <h3 style={styles.resourcesTitle}>📎 المواد المساندة</h3>
              <div style={styles.resourcesList}>
                <div style={styles.resourceItem}>
                  <span style={styles.resourceIcon}>📄</span>
                  <div>
                    <h4 style={styles.resourceName}>ملاحظات الدرس</h4>
                    <span style={styles.resourceInfo}>ملف PDF - 2MB</span>
                  </div>
                  <button style={styles.downloadButton}>تحميل</button>
                </div>
                <div style={styles.resourceItem}>
                  <span style={styles.resourceIcon}>📝</span>
                  <div>
                    <h4 style={styles.resourceName}>أسئلة تقييمية</h4>
                    <span style={styles.resourceInfo}>اختبار تفاعلي</span>
                  </div>
                  <button style={styles.quizButton}>بدء الاختبار</button>
                </div>
              </div>
            </div>
          )}

          {/* تعليقات الطلاب */}
          <div style={styles.commentsSection}>
            <h3 style={styles.commentsTitle}>💬 تعليقات الطلاب</h3>
            <div style={styles.commentInput}>
              <textarea 
                placeholder="شارك رأيك حول هذا الدرس..."
                style={styles.commentTextarea}
                rows="3"
              />
              <button style={styles.submitCommentButton}>إرسال التعليق</button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    color: '#666',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #4CAF50',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a2530 100%)',
    color: 'white',
    padding: '20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  courseInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  backButton: {
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  courseTitle: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  headerStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  progressSection: {
    textAlign: 'left',
  },
  progressLabel: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '5px',
    display: 'block',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  progressBar: {
    width: '150px',
    height: '6px',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.5s ease',
  },
  progressText: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'white',
    minWidth: '40px',
  },
  sidebarToggle: {
    padding: '8px 12px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  content: {
    display: 'flex',
    maxWidth: '1400px',
    margin: '0 auto',
    minHeight: 'calc(100vh - 80px)',
  },
  sidebar: {
    background: 'white',
    borderLeft: '1px solid #e0e0e0',
    transition: 'all 0.3s ease',
    overflowY: 'auto',
    height: 'calc(100vh - 80px)',
    position: 'sticky',
    top: '80px',
  },
  sidebarContent: {
    padding: '10px',
  },
  courseMeta: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '10px',
    marginBottom: '20px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    fontSize: '0.85rem',
    color: '#555',
  },
  metaIcon: {
    fontSize: '1rem',
    minWidth: '24px',
  },
  metaText: {
    flex: 1,
  },
  sectionsList: {
    marginBottom: '20px',
  },
  sectionCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '15px',
    marginBottom: '15px',
    border: '1px solid #e8e8e8',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '0.95rem',
    color: '#333',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionNumber: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  sectionCount: {
    fontSize: '0.8rem',
    color: '#666',
    background: 'rgba(76, 175, 80, 0.1)',
    padding: '2px 8px',
    borderRadius: '12px',
  },
  videosList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  videoItem: {
    padding: '10px',
    background: 'white',
    borderRadius: '8px',
    border: '1px solid #e8e8e8',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  videoItemActive: {
    background: 'rgba(76, 175, 80, 0.1)',
    borderColor: '#4CAF50',
    borderRight: '3px solid #4CAF50',
  },
  videoInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '5px',
  },
  videoHeaderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  videoIcon: {
    fontSize: '0.9rem',
    minWidth: '20px',
  },
  videoTitleItem: {
    fontSize: '0.85rem',
    color: '#333',
    lineHeight: '1.4',
  },
  videoDuration: {
    fontSize: '0.75rem',
    color: '#666',
    background: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: '10px',
  },
  videoMeta: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  videoStatus: {
    fontSize: '0.7rem',
    color: '#666',
    fontStyle: 'italic',
  },
  mainContent: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
  },
  videoPlayerSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
    marginBottom: '25px',
  },
  playerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  playerTitle: {
    fontSize: '1.4rem',
    margin: '0 0 10px 0',
    color: '#333',
    fontWeight: '600',
    flex: 1,
  },
  playerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  nextButton: {
    padding: '8px 16px',
    background: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  videoStatusBadge: {
    fontSize: '0.8rem',
    padding: '4px 10px',
    borderRadius: '12px',
    background: 'rgba(76, 175, 80, 0.1)',
    color: '#2E7D32',
    fontWeight: '500',
  },
  videoContainer: {
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#000',
    marginBottom: '25px',
  },
  videoPlayer: {
    width: '100%',
    height: '500px',
    display: 'block',
  },
  noVideo: {
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    background: '#f5f5f5',
  },
  noVideoIcon: {
    fontSize: '4rem',
    marginBottom: '15px',
  },
  noVideoText: {
    fontSize: '1.1rem',
    color: '#666',
  },
  descriptionSection: {
    marginTop: '25px',
  },
  descriptionTitle: {
    fontSize: '1.2rem',
    margin: '0 0 15px 0',
    color: '#333',
    fontWeight: '600',
  },
  descriptionText: {
    fontSize: '1rem',
    color: '#555',
    lineHeight: '1.6',
    margin: 0,
  },
  noVideoSelected: {
    height: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
  },
  noVideoSelectedIcon: {
    fontSize: '4rem',
    marginBottom: '20px',
  },
  noVideoSelectedTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#333',
  },
  noVideoSelectedText: {
    fontSize: '1.1rem',
    color: '#666',
    margin: 0,
  },
  resourcesSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
    marginBottom: '25px',
  },
  resourcesTitle: {
    fontSize: '1.2rem',
    margin: '0 0 20px 0',
    color: '#333',
    fontWeight: '600',
  },
  resourcesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e8e8e8',
  },
  resourceIcon: {
    fontSize: '1.5rem',
    minWidth: '40px',
  },
  resourceName: {
    margin: '0 0 5px 0',
    fontSize: '1rem',
    color: '#333',
    fontWeight: '500',
  },
  resourceInfo: {
    fontSize: '0.85rem',
    color: '#666',
  },
  downloadButton: {
    marginRight: 'auto',
    padding: '6px 12px',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  quizButton: {
    marginRight: 'auto',
    padding: '6px 12px',
    background: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  commentsSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
  },
  commentsTitle: {
    fontSize: '1.2rem',
    margin: '0 0 20px 0',
    color: '#333',
    fontWeight: '600',
  },
  commentInput: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  commentTextarea: {
    padding: '15px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
  },
  submitCommentButton: {
    alignSelf: 'flex-start',
    padding: '10px 25px',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      .back-btn-hover:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        transform: translateX(5px);
      }
      
      .sidebar-toggle-hover:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        transform: scale(1.05);
      }
      
      .next-btn-hover:hover {
        background: #1976D2 !important;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(33, 150, 243, 0.3);
      }
      
      .video-item-hover:hover {
        background: rgba(76, 175, 80, 0.05) !important;
        border-color: #4CAF50 !important;
        transform: translateX(-5px);
      }
      
      .video-item-active:hover {
        background: rgba(76, 175, 80, 0.15) !important;
      }
      
      .download-btn-hover:hover {
        background: #2E7D32 !important;
        transform: translateY(-2px);
      }
      
      .quiz-btn-hover:hover {
        background: #1976D2 !important;
        transform: translateY(-2px);
      }
      
      .submit-comment-btn-hover:hover {
        background: #2E7D32 !important;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3);
      }
      
      @media (max-width: 1024px) {
        .main-content-responsive {
          padding: 15px !important;
        }
        
        .video-player-responsive {
          height: 400px !important;
        }
      }
      
      @media (max-width: 768px) {
        .header-content-responsive {
          flex-direction: column !important;
          gap: 15px !important;
          text-align: center !important;
        }
        
        .course-info-responsive {
          flex-direction: column !important;
          gap: 10px !important;
        }
        
        .header-stats-responsive {
          width: 100%;
          justify-content: space-between !important;
        }
        
        .player-header-responsive {
          flex-direction: column !important;
          gap: 15px !important;
          align-items: flex-start !important;
        }
        
        .player-actions-responsive {
          width: 100%;
          justify-content: space-between !important;
        }
        
        .video-player-responsive {
          height: 300px !important;
        }
        
        .sidebar-responsive {
          position: fixed !important;
          top: 80px !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 1000 !important;
          box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
        }
      }
      
      @media (max-width: 480px) {
        .progress-bar-responsive {
          width: 100px !important;
        }
        
        .video-player-responsive {
          height: 250px !important;
        }
        
        .resource-item-responsive {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 10px !important;
        }
        
        .buttons-responsive {
          align-self: stretch !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      const elements = [
        { selector: '[style*="backButton"]', className: 'back-btn-hover' },
        { selector: '[style*="sidebarToggle"]', className: 'sidebar-toggle-hover' },
        { selector: '[style*="nextButton"]', className: 'next-btn-hover' },
        { selector: '[style*="videoItem"]', className: 'video-item-hover' },
        { selector: '[style*="downloadButton"]', className: 'download-btn-hover' },
        { selector: '[style*="quizButton"]', className: 'quiz-btn-hover' },
        { selector: '[style*="submitCommentButton"]', className: 'submit-comment-btn-hover' },
      ];
      
      elements.forEach(({ selector, className }) => {
        document.querySelectorAll(selector).forEach(el => {
          el.className = (el.className || '') + ' ' + className;
        });
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const responsiveElements = [
          { selector: '[style*="headerContent"]', className: 'header-content-responsive' },
          { selector: '[style*="courseInfo"]', className: 'course-info-responsive' },
          { selector: '[style*="headerStats"]', className: 'header-stats-responsive' },
          { selector: '[style*="playerHeader"]', className: 'player-header-responsive' },
          { selector: '[style*="playerActions"]', className: 'player-actions-responsive' },
          { selector: '[style*="videoPlayer"]', className: 'video-player-responsive' },
          { selector: '[style*="sidebar"]', className: 'sidebar-responsive' },
        ];
        
        responsiveElements.forEach(({ selector, className }) => {
          const element = document.querySelector(selector);
          if (element) element.className = (element.className || '') + ' ' + className;
        });
      }
      
      if (window.innerWidth <= 480) {
        const progressBar = document.querySelector('[style*="progressBar"]');
        const resourceItems = document.querySelectorAll('[style*="resourceItem"]');
        const downloadButtons = document.querySelectorAll('[style*="downloadButton"]');
        const quizButtons = document.querySelectorAll('[style*="quizButton"]');
        
        if (progressBar) progressBar.className = (progressBar.className || '') + ' progress-bar-responsive';
        
        resourceItems.forEach(el => {
          el.className = (el.className || '') + ' resource-item-responsive';
        });
        
        [...downloadButtons, ...quizButtons].forEach(el => {
          el.className = (el.className || '') + ' buttons-responsive';
        });
      }
    }, 200);
  }, 100);
}